import { useState, FormEvent, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { PublicClientApplication } from '@azure/msal-browser';
import { userLoginRequest, apiConfig } from '../config/authConfig';
import { setMsalInstance } from '../services/api';

export default function JoinPage() {
  const navigate = useNavigate();
  const { instance } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  const [email, setEmail] = useState('');
  const [accessCode, setAccessCode] = useState('');
  const [showAccessCodeField, setShowAccessCodeField] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Set MSAL instance for API calls
  setMsalInstance(instance as PublicClientApplication);

  // If already authenticated, redirect to chat
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/chat', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleEmailSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }

    setIsLoading(true);

    try {
      // Check if email exists and is active
      const response = await fetch(`${apiConfig.baseUrl}/users/check-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });

      if (!response.ok) {
        const data = await response.json();
        setError(data.message || 'Email not found. Please contact your administrator.');
        setIsLoading(false);
        return;
      }

      const data = await response.json();
      
      // If user is already linked, redirect to login
      if (data.isLinked) {
        // Existing user - redirect to login
        await instance.loginRedirect({
          ...userLoginRequest,
          redirectUri: `${window.location.origin}/auth-callback`,
          loginHint: email.trim(),
        });
      } else {
        // New user - show access code field to verify identity
        setShowAccessCodeField(true);
        setIsLoading(false);
      }
      
      // Code after redirect won't execute
    } catch (err: any) {
      console.error('Sign in failed:', err);
      setError('Something went wrong. Please try again.');
      setIsLoading(false);
    }
  };

  const handleAccessCodeSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!accessCode.trim()) {
      setError('Please enter your access code');
      return;
    }

    setIsLoading(true);

    try {
      // Validate the access code
      const response = await fetch(`${apiConfig.baseUrl}/users/validate-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          email: email.trim(),
          accessCode: accessCode.trim().toUpperCase() 
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        setError(data.message || 'Invalid access code');
        setIsLoading(false);
        return;
      }

      await response.json();
      
      // Access code valid - redirect to signup
      await instance.loginRedirect({
        ...userLoginRequest,
        redirectUri: `${window.location.origin}/auth-callback`,
        loginHint: email.trim(),
        prompt: 'create', // Forces signup flow in External ID
      });
      
      // Code after redirect won't execute
    } catch (err: any) {
      console.error('Sign in failed:', err);
      setError('Something went wrong. Please try again.');
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-wise-light to-white flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img src="/simplytechwise-logo.png" alt="Simply TechWise" className="h-16 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-wise-dark">
            WiseAssist
            <sub className="text-sm text-gray-500 ml-1">v{import.meta.env.VITE_APP_VERSION || '0.1.0'}</sub>
          </h1>
          <p className="text-gray-600 mt-2">Your friendly tech support assistant</p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {!showAccessCodeField ? (
            <form onSubmit={handleEmailSubmit}>
              <h2 className="text-xl font-semibold text-gray-800 mb-2">
                Sign In 👋
              </h2>
              <p className="text-gray-600 mb-6">
                Enter your email address to sign in. We'll send you a secure link to access your account.
              </p>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {error}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:border-wise focus:ring-2 focus:ring-wise/20 outline-none"
                  autoFocus
                  disabled={isLoading}
                  required
                />
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full mt-6 px-6 py-4 bg-wise text-white text-lg font-medium rounded-xl hover:bg-wise-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Checking...
                  </>
                ) : (
                  'Continue'
                )}
              </button>

              <p className="text-xs text-gray-500 text-center mt-4">
                ℹ️ First time? You'll need your access code from your administrator
              </p>
            </form>
          ) : (
            <form onSubmit={handleAccessCodeSubmit}>
              <h2 className="text-xl font-semibold text-gray-800 mb-2">
                Enter Access Code 🔑
              </h2>
              <p className="text-gray-600 mb-6">
                This is your first time signing in. Please enter the access code provided by your administrator.
              </p>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {error}
                </div>
              )}

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  value={email}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-gray-50"
                  disabled
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Access Code
                </label>
                <input
                  type="text"
                  value={accessCode}
                  onChange={(e) => setAccessCode(e.target.value.toUpperCase())}
                  placeholder="XXXXXXXX"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:border-wise focus:ring-2 focus:ring-wise/20 outline-none font-mono text-lg tracking-wider text-center"
                  autoFocus
                  disabled={isLoading}
                  maxLength={8}
                  required
                />
                <p className="mt-2 text-sm text-gray-500 text-center">
                  8-character code from your administrator
                </p>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowAccessCodeField(false);
                    setAccessCode('');
                    setError(null);
                  }}
                  className="flex-1 px-6 py-4 bg-gray-100 text-gray-700 text-lg font-medium rounded-xl hover:bg-gray-200 transition-colors"
                  disabled={isLoading}
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="flex-1 px-6 py-4 bg-wise text-white text-lg font-medium rounded-xl hover:bg-wise-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    'Verify & Sign Up'
                  )}
                </button>
              </div>
            </form>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-gray-500 text-sm mt-6">
          Need help? Contact your administrator who set up WiseAssist.
        </p>
      </div>
    </div>
  );
}
